import { makeAutoObservable } from 'mobx';
import {DomainStore} from 'shared/store';
import {t} from 'shared/core';
import {auth, types, endpoints} from 'main/tools';
import {Company} from 'main/stores/company';
import {CompanyLink} from 'main/stores/company_links';
import {StaffCounts} from 'main/stores/hr_dashboard/';
import {TimeOffAccount} from 'main/stores/time_off';
import _ from 'lodash';
import * as Sentry from '@sentry/react';
import {CompanyFile, CompanyFileFolder} from 'main/stores/company_files';
import {NmbrPaySchedule} from 'main/stores/nmbr';
import moment from 'moment';

class HRDashboardPageState {
  emojiReady = false;
  whatsGoingOnWindowSize = 7;

  calendarEvents = [];
  company;
  staffCount;
  timeOffAccounts = [];
  companyLinks = [];
  folderlessFiles = [];
  companyFileFolders = [];
  payPeriod = {};
  paySchedules = [];
  loadingPaySchedules = false;
  payScheduleErrors = false;
  insightsInboxSummary;
  insightsPTOSummary;
  insightsNewHireSummary;
  insightsJobApplicantSummary;

  store = new DomainStore();

  constructor() {
    makeAutoObservable(this, {
      store: false,
      emojiReady: false
    });
  }

  endpoints() {
    const today = moment().startOf('day');
    const windowEnd = today.clone().add(this.whatsGoingOnWindowSize, 'days');

    const result = [
      endpoints.CALENDAR_EVENTS.WITH_TYPES.with(
        today.format('YYYY-MM-DD'),
        windowEnd.format('YYYY-MM-DD'),
        ['time_off_request', 'holiday', 'birthday', 'anniversary', 'first_day', 'custom_event']
      ),
      endpoints.COMPANY_LINKS.ALL,
      endpoints.COMPANY_FILES.with(),
      endpoints.COMPANY_FILE_FOLDERS.ALL,
      endpoints.CURRENT_COMPANY
    ];

    if (auth.user.staffCountVisibility) {
      result.push(endpoints.HR_DASHBOARD.STAFF_COUNTS);
    }

    if (auth.employee && auth.employee.hasTimeOffPolicy) {
      result.push(endpoints.TIME_OFF.ACCOUNTS.with(auth.employee.id));
    }

    if (auth.user.admin) {
      result.push(endpoints.HR_DASHBOARD.INSIGHTS.INBOX_SUMMARY);
      result.push(endpoints.HR_DASHBOARD.INSIGHTS.NEW_HIRE_SUMMARY);
      result.push(endpoints.HR_DASHBOARD.INSIGHTS.PTO_SUMMARY);
      result.push(endpoints.HR_DASHBOARD.INSIGHTS.JOB_APPLICANT_SUMMARY);
    }

    if (auth.hasAccess('::PAYROLL') && auth.company.payrollEnabled) {
      result.push(endpoints.PAY_PERIOD.NEXT_PAY_DATE_PERIOD);
    }

    if (auth.hasAccess('::MANAGE_ANNOUNCEMENTS')) {
      result.push(endpoints.EMPLOYEES.ALL);
    }

    return result;
  }

  populateStore() {
    this.company = new Company(this.store._getSingle(types.COMPANY));
    this.calendarEvents = this.store._getAll(types.CALENDAR_EVENT);
    this.insightsInboxSummary = this.store._getSingle(types.HR_DASHBOARD.INSIGHTS.INBOX_SUMMARY);
    this.insightsPTOSummary = this.store._getSingle(types.HR_DASHBOARD.INSIGHTS.PTO_SUMMARY);
    this.insightsNewHireSummary = this.store._getSingle(types.HR_DASHBOARD.INSIGHTS.NEW_HIRE_SUMMARY);
    this.insightsJobApplicantSummary = this.store._getSingle(types.HR_DASHBOARD.INSIGHTS.JOB_APPLICANT_SUMMARY);
    this.staffCount = new StaffCounts(this.store._getSingle(types.HR_DASHBOARD.STAFF_COUNTS));
    this.companyLinks = this.store._getAll(types.COMPANY_LINK, CompanyLink);
    this.folderlessFiles = this.store._getAll(types.COMPANY_FILE, CompanyFile);
    this.companyFileFolders = this.store._getAll(types.COMPANY_FILE_FOLDER, CompanyFileFolder);
    this.timeOffAccounts = this.store._getAll(types.TIME_OFF.ACCOUNT).map(account => new TimeOffAccount(account));
    this.payPeriod = this.store._getSingle(types.PAY_PERIOD);
  }

  async load() {
    if ((auth.hasAccess('::PAYROLL') || auth.employee.hasNmbrPayroll) && auth.company.nmbrEnabled) {
      this.loadPaySchedules();
    }

    const _endpoints = this.endpoints();
    const [responses, emojiModules] = await Promise.all([
      this.store._compose(_endpoints, {failSilently: true}),
      Promise.all([import('emoji-mart'), import('@emoji-mart/data')])
        .catch(e => {
          if (!(/Loading chunk .* failed/.test(e.message))) {
            throw e;
          }
        })
    ]);

    this.populateStore();
    if (emojiModules) {
      const [{init}, data] = emojiModules;
      await init({data: data.default});
      this.emojiReady = true;
    }

    _.each(responses, r => {
      if (!r.failedSilently) return;

      Sentry.captureException(r.e);
    });
  }

  async loadPaySchedules() {
    this.loadingPaySchedules = true;

    try {
      await this.store._compose([
        endpoints.NMBR.PAY_SCHEDULES.WITH_NEXT_PAYROLLS
      ]);
      this.paySchedules = this.store._getAll(types.NMBR.PAY_SCHEDULE, NmbrPaySchedule);
    } catch {
      this.payScheduleErrors = true;
    } finally {
      this.loadingPaySchedules = false;
    }
  }

  get greeting() {
    const currentHours = new Date().getHours();
    if (currentHours < 12) {
      return t('hr_dashboard.greetings.GOOD_MORNING', {name: auth.user.firstName});
    }
    if (currentHours < 18) {
      return t('hr_dashboard.greetings.GOOD_AFTERNOON', {name: auth.user.firstName});
    }

    return t('hr_dashboard.greetings.GOOD_EVENING', {name: auth.user.firstName});
  }

  get sortedCompanyLinks() {
    return _.sortBy(this.companyLinks, 'order');
  }
}

export default HRDashboardPageState;
